/**
 * studioList — the PURE data layer behind the Studio "My games" grid: the
 * work-surface sort orders and the owner/editor ownership filter, kept free of
 * React and Firestore so the list behavior is directly unit-testable (the same
 * discipline as gallery/model.ts).
 *
 * Studio deliberately does NOT reuse the gallery's `trending` sort: your own
 * list is a workbench, not a discovery feed. The sorts here answer "what did I
 * touch last", "what gets played", "what's loved", "what have I worked hardest
 * on", and "what needs my attention" (status groups builds/failures ahead of
 * live games).
 */
import type { Creation } from '@djgg/creations';

/** The studio grid's sort orders (work-surface set — no `trending`). */
export type StudioSort = 'updated' | 'played' | 'liked' | 'iterated' | 'status';

/** Ownership filter: everything, only games I own, or only games I merely edit. */
export type StudioOwnership = 'all' | 'owned' | 'editor';

/** The sort tabs, in display order. */
export const STUDIO_SORTS: ReadonlyArray<readonly [StudioSort, string]> = [
  ['updated', 'Last updated'],
  ['played', 'Most played'],
  ['liked', 'Most liked'],
  ['iterated', 'Most iterated'],
  ['status', 'Status'],
];

/** The ownership filter chips, in display order. */
export const STUDIO_OWNERSHIP: ReadonlyArray<readonly [StudioOwnership, string]> = [
  ['all', 'All'],
  ['owned', 'Owned'],
  ['editor', 'Editing'],
];

/** True when `uid` is the creator (owner) of `c` — false for editor-only members. */
export function isOwned(c: Pick<Creation, 'creatorUid'>, uid: string): boolean {
  return c.creatorUid === uid;
}

/** Status ordering for the `status` sort: what needs attention first —
 *  in-progress builds, then failures, then unpublished drafts, then live. */
const STATUS_RANK: Record<Creation['status'], number> = {
  building: 0,
  failed: 1,
  draft: 2,
  live: 3,
};

/** Apply the ownership filter to a member list (the `myCreations` result, which
 *  includes games the user only edits). */
export function filterOwnership<T extends Pick<Creation, 'creatorUid'>>(
  games: T[],
  uid: string,
  ownership: StudioOwnership,
): T[] {
  if (ownership === 'all') return games;
  const wantOwned = ownership === 'owned';
  return games.filter((g) => isOwned(g, uid) === wantOwned);
}

type Sortable = Pick<
  Creation,
  'status' | 'updatedAt' | 'playsCount' | 'likesCount' | 'latestVersion' | 'title'
>;

/** Order a copy of `games` by `sort`; every sort tiebreaks by recency then
 *  title so the order is stable and deterministic. Does not mutate the input. */
export function sortStudioGames<T extends Sortable>(games: T[], sort: StudioSort): T[] {
  const key = (g: T): number => {
    switch (sort) {
      case 'played':
        return g.playsCount ?? 0;
      case 'liked':
        return g.likesCount ?? 0;
      // The same number the card shows as "vN" — how many versions this game has
      // been through, so the order matches what the creator can see.
      case 'iterated':
        return g.latestVersion ?? 0;
      case 'status':
        // Negated so the ascending comparator below puts rank 0 (building) first.
        return -STATUS_RANK[g.status];
      case 'updated':
      default:
        return g.updatedAt;
    }
  };
  return [...games].sort((a, b) => {
    const d = key(b) - key(a);
    if (d !== 0) return d;
    const recency = b.updatedAt - a.updatedAt;
    if (recency !== 0) return recency;
    return a.title.localeCompare(b.title);
  });
}
